import React, { useCallback, useRef } from 'react';

import { Panorama } from '../../../../../PanoramaLibrary/components/Panorama';
import { ContentWrapper } from '../../../../components/ContentWrapper/ContentWrapper';
import { HintButton } from '../../../../components/shared/HintButton';
import { DraftPanoramaPortalId } from '../../../../features/EditItem/components/overlays/LocationSettings/DraftPanoramaPortal';
import { useLocatingInDraftPanorama } from '../../../../features/EditItem/components/overlays/LocationSettings/hooks/useLocatingInDraftPanorama';
import { useContentRequirementsContext } from '../../../../features/RestrictNavigation/ContentRequirementsProvider';
import { getMainProjectPanorama } from '../../../../globalPanoramaAccessor';
import { useLocatingHiddenAreaInStudent } from '../../../../hooks/useLocatingHiddenAreaInStudent';
import { useSwitch } from '../../../../hooks/useSwitch';
import { getCurrentItem } from '../../../../store/selectors/getCurrentItem';
import { useStudentSelector } from '../../../../store/selectors/useStudentSelector';
import { HiddenAreaWidget, isQuiz } from '@ael/core-api';
import { StudentNavigationBar } from '@ael/shared-components-library';
import { solid } from '@fortawesome/fontawesome-svg-core/import.macro';
import classNames from 'classnames';
import * as ReactDOM from 'react-dom';

import './LocatingHiddenAreaWidget.scss';

const PANORAMA_ID = 'HiddenAreaWidgetPanoramaId';
const TARGET_DIV_ID = 'IdForHiddenAreaWidget';

interface LocatingHiddenAreaWidgetProps {
  widget: HiddenAreaWidget;
  isCompleted?: boolean;
  onDone: (success: boolean) => void;
}

export const LocatingHiddenAreaWidget: React.FC<LocatingHiddenAreaWidgetProps> = ({ widget, isCompleted, onDone }) => {
  const currentItem = useStudentSelector(getCurrentItem);
  const draftPanoPortal = document.getElementById(DraftPanoramaPortalId);
  const { isOn: isHiddenAreaCompleted, turnOn: setHiddenAreaCompleted } = useSwitch(isCompleted);
  const { submitWidget } = useContentRequirementsContext();
  const toolbarRef = useRef(null);

  const currentLocation = widget.value.data.locations[0];

  const { isOn: panoVisible, turnOn: setPanoVisible } = useSwitch();
  const { isOn: isShowingHintButton, turnOn: showHintButton } = useSwitch(false);

  const allowHintButton = (currentItem && !isQuiz(currentItem)) || isShowingHintButton || false;

  const finishLocating = React.useCallback(
    (isCompleted?: boolean) => {
      onDone(isCompleted === true || isHiddenAreaCompleted);
    },
    [isHiddenAreaCompleted, onDone]
  );

  const { draftPanorama } = useLocatingInDraftPanorama({
    panoramaId: PANORAMA_ID,
    targetId: TARGET_DIV_ID,
    originalPanorama: getMainProjectPanorama(),
    onReady: setPanoVisible,
    view: {
      sceneIndex: currentLocation.cameraPosition
        ? currentLocation.cameraPosition.sceneIndex
        : getMainProjectPanorama().getPosition().view.sceneIndex,
      tourSceneGuid:
        currentLocation.cameraPosition && currentLocation.cameraPosition.tourSceneGuid
          ? currentLocation.cameraPosition.tourSceneGuid
          : getMainProjectPanorama().getPosition().view.tourSceneGuid,
    },
    isManager: false,
  });

  const confirmPosition = useCallback(
    (correct: boolean) => {
      if (correct) {
        submitWidget(widget, true);
        setTimeout(() => {
          setHiddenAreaCompleted();
          finishLocating(true);
        }, 750);
      } else {
        submitWidget(widget, false);
        showHintButton();
      }
    },
    [finishLocating, setHiddenAreaCompleted, showHintButton, submitWidget, widget]
  );

  const { displayHint } = useLocatingHiddenAreaInStudent(
    draftPanorama,
    currentLocation.cameraPosition,
    currentLocation.location,
    confirmPosition
  );

  if (draftPanoPortal) {
    return ReactDOM.createPortal(
      <div
        className={classNames('fade-enter', {
          'fade-enter-active': panoVisible,
        })}
      >
        <Panorama targetId={TARGET_DIV_ID} />
        {/* TODO change to currentLocation.hint instead of question text, when support for multiple locations is added */}
        <div className="locating-hidden-area-widget__question">{widget.value.data.questionText}</div>
        {allowHintButton && (
          <div className="locating-hidden-area-widget__hint-wrapper">
            <HintButton onClick={displayHint} />
          </div>
        )}
        <ContentWrapper>
          <ContentWrapper.Toolbar ref={toolbarRef}>
            <StudentNavigationBar ref={toolbarRef} type="StudentFull">
              <StudentNavigationBar.ButtonBox justifyEnd hasLabels>
                <StudentNavigationBar.Button
                  big
                  onClick={finishLocating}
                  label="Close"
                  icon={solid('times')}
                  showLabel
                />
              </StudentNavigationBar.ButtonBox>
            </StudentNavigationBar>
          </ContentWrapper.Toolbar>
        </ContentWrapper>
      </div>,
      draftPanoPortal
    );
  }

  return null;
};
